import { EnumValueDisplay, FormSelect } from '@lib/common';
import {
  CostCenterFieldConfiguration,
  CostCenterFieldStatus,
  CostCenterOption,
  CostCenterType,
} from '@lib/state';
import { Grid, MenuItem } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CostCenterOptionsListForm } from './cost-center-options-list-form';

interface Props {
  index: number;
  costCenterConfig: CostCenterFieldConfiguration;
  accountId?: string;
  costCenterOptions?: Array<CostCenterOption>;
}

export function CostCenterSettings({
  index,
  costCenterConfig,
  accountId,
  costCenterOptions = [],
}: Props) {
  const { watch, register, setValue } = useFormContext();

  const status = watch(`fields[${index}].status`) as CostCenterFieldStatus;
  const type = watch(`fields[${index}].type`) as CostCenterType;

  //#region Cost Center Options Handlers
  useEffect(() => {
    register('costCenterOptions');
  }, [register]);

  const options: Array<CostCenterOption> = watch('costCenterOptions', costCenterOptions);

  const currentOptions = useMemo(
    () => options.filter(x => x.costCenterFieldKey === costCenterConfig.key),
    [options, costCenterConfig]
  );

  useEffect(() => {
    if (costCenterOptions) {
      setValue('costCenterOptions', costCenterOptions);
    }
  }, [costCenterOptions, setValue]);

  const handleOptionChange = useCallback(
    (optionsList: Array<CostCenterOption>, costCenterKey: string) => {
      if (options?.length)
        optionsList = optionsList.concat(
          options.filter(x => x.costCenterFieldKey !== costCenterKey)
        );
      setValue('costCenterOptions', optionsList);
    },
    [setValue, options]
  );
  // #endregion

  return (
    <Grid key={costCenterConfig.key} item md={3} xs={12}>
      <input
        type="hidden"
        name={`fields[${index}].key`}
        ref={register}
        defaultValue={costCenterConfig.key ?? ''}
      />
      <input
        type="hidden"
        name={`fields[${index}].label`}
        ref={register}
        defaultValue={costCenterConfig.label ?? ''}
      />
      <FormSelect
        name={`fields[${index}].status`}
        label={costCenterConfig.label}
        fullWidth
        defaultValue={costCenterConfig.status}
      >
        <MenuItem value={CostCenterFieldStatus.Disabled}>Disabled</MenuItem>
        <MenuItem value={CostCenterFieldStatus.Enabled}>Enabled</MenuItem>
        <MenuItem value={CostCenterFieldStatus.Required}>Required</MenuItem>
      </FormSelect>
      {costCenterConfig.selectEnabled && (
        <FormSelect
          name={`fields[${index}].type`}
          label="Type"
          fullWidth
          disabled={status === CostCenterFieldStatus.Disabled}
          defaultValue={costCenterConfig.type}
        >
          {Object.values(CostCenterType).map(x => (
            <MenuItem key={x} value={x}>
              <EnumValueDisplay value={x} />
            </MenuItem>
          ))}
        </FormSelect>
      )}
      {type === CostCenterType.Select && status !== CostCenterFieldStatus.Disabled && (
        <CostCenterOptionsListForm
          key={index}
          costCenterKey={costCenterConfig.key}
          accountId={accountId}
          costCenterOptions={currentOptions}
          handleOptionChange={handleOptionChange}
        />
      )}
    </Grid>
  );
}
